import React from "react"
import { CircularProgress, Container, Grid } from "@material-ui/core"
import ClearIcon from '@material-ui/icons/Clear'
import { Add, GetApp, Settings } from "@material-ui/icons"
import searchStyles from '../AgentsComponent/styles.module.css'
import styles from './styles.module.css'
import { BlueInverseBtn, GreyInverseBtn } from "../Styled/StyledButtons"
import { IBlockFilters } from "../../models/RobocallBlocks/BlockedNumber"

interface IProps {
    filters: IBlockFilters
    searchStatus: string
    vetoConflictCount: number | null
    onChange: (filters: IBlockFilters) => void
    onAdd: () => void
    onExport: () => void
}

/**
 * Filters for the review queue.
 *
 * `veto_conflicts` is a first-class toggle rather than something to construct by hand: it
 * is the only way to reach numbers whose signature contradicts their veto, and automation
 * is forbidden from touching those, so if a person does not go looking they stay
 * unresolved forever.
 */
const ControlPanel = (props: IProps) => {
    const set = (patch: Partial<IBlockFilters>) => props.onChange({ ...props.filters, ...patch })

    return (
        <Container maxWidth={false}>
            <div className={styles.Panel}>
                <Grid container spacing={1} alignItems="flex-end">
                    <Grid item xs={12} md={3}>
                        <div className={searchStyles.filters__search}>
                            {props.searchStatus !== 'loading' ?
                                <span
                                    className={searchStyles.filters__search_clear}
                                    onClick={() => set({ q: '' })}>
                                    <ClearIcon className={searchStyles.filters__search_loading} />
                                </span>
                                :
                                <span className={searchStyles.filters__search_clear}>
                                    <CircularProgress size={24} className={searchStyles.filters__search_loading} />
                                </span>
                            }
                            <input
                                type="text"
                                placeholder="Search number"
                                onChange={(e) => set({ q: e.target.value })}
                                value={props.filters.q || ''}
                                className={searchStyles.filters__search__inputSearch}
                            />
                        </div>
                    </Grid>

                    <Grid item xs={6} md={2}>
                        <select
                            className={styles.Select}
                            value={props.filters.status || ''}
                            onChange={(e) => set({ status: e.target.value as any })}
                        >
                            <option value="">Any state</option>
                            <option value="candidate">Potential (candidate)</option>
                            <option value="active">Confirmed (blocking)</option>
                            <option value="expired">Expired</option>
                            <option value="released">Cleared</option>
                        </select>
                    </Grid>

                    <Grid item xs={6} md={2}>
                        <select
                            className={styles.Select}
                            value={props.filters.source || ''}
                            onChange={(e) => set({ source: e.target.value as any })}
                        >
                            <option value="">Any source</option>
                            <option value="agent">Agent flagged</option>
                            <option value="auto">Scheduled scan</option>
                            <option value="manual">Manual</option>
                            <option value="imported">Imported</option>
                        </select>
                    </Grid>

                    <Grid item xs={6} md={2}>
                        <select
                            className={styles.Select}
                            value={props.filters.min_score === '' || props.filters.min_score === undefined
                                ? ''
                                : String(props.filters.min_score)}
                            onChange={(e) => set({ min_score: e.target.value === '' ? '' : parseInt(e.target.value, 10) })}
                        >
                            <option value="">Any score</option>
                            <option value="35">score ≥ 35</option>
                            <option value="55">score ≥ 55 (auto-block bar)</option>
                            <option value="70">score ≥ 70</option>
                        </select>
                    </Grid>

                    <Grid item xs={6} md={3}>
                        <label className={styles.Evidence}>
                            <input
                                type="checkbox"
                                checked={!!props.filters.veto_conflicts}
                                onChange={(e) => set({ veto_conflicts: e.target.checked })}
                            />
                            &nbsp;Veto conflicts only
                            {props.vetoConflictCount !== null && props.vetoConflictCount > 0
                                ? ` (${props.vetoConflictCount})`
                                : ''}
                        </label>
                        <div className={styles.Hint}>
                            Signature contradicts the veto. Automation cannot act on these —
                            only you can.
                        </div>
                    </Grid>

                    <Grid item xs={12} md={3}>
                        <BlueInverseBtn onClick={props.onAdd} variant="contained" fullWidth={true}>
                            <Add />&nbsp;Block a number
                        </BlueInverseBtn>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <GreyInverseBtn onClick={props.onExport} variant="contained" fullWidth={true}>
                            <GetApp />&nbsp;CSV
                        </GreyInverseBtn>
                    </Grid>
                    <Grid item xs={6} md={2}>
                        <GreyInverseBtn variant="contained" fullWidth={true} href="/robocall-settings">
                            <Settings />&nbsp;Settings
                        </GreyInverseBtn>
                    </Grid>
                </Grid>
            </div>
        </Container>
    )
}

export default ControlPanel
